import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './EditAccountPage.scss';
import SvgIcons from '../../../services/SvgIcons';
import { validateField } from '../../../services/inputCheck';
import BackgroundImageWithSkeleton from '../../../components/BackgroundImageWithSkeleton/BackgroundImageWithSkeleton';
import Skeleton from '../../../components/Skeleton/Skeleton';
import { imageCompressor } from '../../../services/imageCompressor';
import { strapiAuthService } from '../../../services/api/strapiAuth';
import { useAuth } from '../../../contexts/AuthContext';

const EditAccountPage = () => {
    const navigate = useNavigate();
     const { isAuthenticated, isLoading: authLoading, updateUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        telephone: '',
        codePostal: '',
        ville: '',
        pays: '',
        picture: null,
        newsletter: false
    });
    const [errors, setErrors] = useState({});
    const [previewImage, setPreviewImage] = useState(null);
    const [originalPicture, setOriginalPicture] = useState(null); // Nouvelle state pour l'image originale

    useEffect(() => {
        window.scroll(0, 0);
    }, []);

    useEffect(() => {
        if (authLoading) return; // Attendre que l'auth soit initialisée
        if (!isAuthenticated) {
            navigate('/connexion');
            return;
        }
        fetchUserData();
    }, [authLoading, isAuthenticated]);

    const fetchUserData = async () => {
        try {
            setLoading(true);

            // Récupérer le profil utilisateur
            const userData = await strapiAuthService.getProfile();

            // Remplir le formulaire avec les données
            setFormData({
                firstname: userData.firstname || '',
                lastname: userData.lastname || '',
                email: userData.email || '',
                telephone: userData.telephone || '',
                codePostal: userData.codePostal || '',
                ville: userData.ville || '',
                pays: userData.pays || '',
                picture: userData.picture,
                newsletter: userData.newsletter || false
            });

            // Gérer l'aperçu de l'image
            if (userData.picture) {
                setOriginalPicture(userData.picture);
                setPreviewImage(userData.picture);
            }

        } catch (error) {
            console.error('Error fetching user data:', error);
            // Ne rediriger que si c'est vraiment un problème d'authentification
            if (error.message?.includes('401') || error.message?.includes('Token')) {
                navigate('/connexion');
            } else {
                setErrors(prev => ({ ...prev, submit: 'Erreur lors du chargement du profil. Réessaie.' }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Validation en temps réel
        const error = validateField(name, newValue);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                // Vérifier d'abord la taille du fichier original
                if (file.size > 10 * 1024 * 1024) { // 10MB
                    setErrors(prev => ({
                        ...prev,
                        picture: 'L\'image ne doit pas dépasser 10MB'
                    }));
                    return;
                }

                // Compresser l'image
                const compressedBlob = await imageCompressor.compressProfilePicture(file);
                
                // Convertir le blob en base64
                const reader = new FileReader();
                reader.readAsDataURL(compressedBlob);
                reader.onloadend = () => {
                    const base64Result = reader.result;
                    console.log('Base64 result:', base64Result);
                    
                    // Mettre à jour l'aperçu avec la nouvelle image
                    setPreviewImage(base64Result);
                    setFormData(prev => ({
                        ...prev,
                        picture: base64Result
                    }));
                    
                    // Effacer les erreurs précédentes
                    setErrors(prev => ({
                        ...prev,
                        picture: null
                    }));
                };
            } catch (error) {
                console.error('Erreur compression image:', error);
                setErrors(prev => ({
                    ...prev,
                    picture: 'Erreur lors du traitement de l\'image'
                }));
            }
        }
    };

    const removeImage = () => {
        setPreviewImage(null);
        setFormData(prev => ({
            ...prev,
            picture: 'remove'
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            // Validation uniquement sur les champs obligatoires
            const optionalFields = ['picture', 'newsletter', 'telephone', 'codePostal', 'ville', 'pays'];
            const validationErrors = {};
            Object.keys(formData).forEach(field => {
                if (!optionalFields.includes(field)) {
                    const error = validateField(field, formData[field]);
                    if (error) validationErrors[field] = error;
                }
            });

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            await strapiAuthService.updateProfile(formData);

            // Mettre à jour le user dans le context
            await updateUser();

            sessionStorage.setItem('profileUpdateMessage', 'Ton profil a été mis à jour avec succès');

            // Navigation simple (sans rechargement !)
            navigate('/mon-compte');
            
        } catch (error) {
            setErrors(prev => ({
                ...prev,
                submit: error.message || 'Une erreur est survenue lors de la mise à jour'
            }));
        } finally {
            setSaving(false);
        }
    };

    const renderImagePreview = () => {
        if (loading) {
            return (
                <Skeleton 
                    width="120px"
                    height="120px"
                    borderRadius="60px"
                    bkgColor="#EEE6DB"
                    animColor="#F5F0ED"
                />
            );
        }

        if (previewImage) {
            // Si c'est une string base64 (nouvelle image)
            if (typeof previewImage === 'string' && previewImage.startsWith('data:')) {
                return (
                    <img
                        src={previewImage}
                        alt="Aperçu"
                        style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '60px',
                            objectFit: 'cover'
                        }}
                    />
                );
            } 
            // Si c'est un objet picture Strapi (image existante)
            else if (typeof previewImage === 'object' && previewImage.url) {
                return (
                    <BackgroundImageWithSkeleton
                        picture={previewImage}
                        width="120px"
                        height="120px"
                        positionMobile="static"
                        positionDesktop="static"
                        borderRadius="60px"
                    />
                );
            }
        }

        // Image par défaut
        return (
            <SvgIcons
                name="user"
                width="100"
                height="100"
                color="#6D4F47"
            />
        );
    };

    return (
        <div className="edit-account-page" aria-label='arrière-plan beige'>
            <Helmet>
                <title>Modifier mon profil | Soutien émotionnel des mamans | Grandir et s'élever ensemble</title>
            </Helmet>

            <div className="edit-account-page__container">
                <Link to="/mon-compte" className="edit-account-page__back">
                    <SvgIcons name="chevron_back" width="20" height="20" color="currentColor" />
                    Mon compte
                </Link>
                <h1>Modifier mon profil</h1>

                <form onSubmit={handleSubmit} className="edit-account-page__form">
                    <div className="edit-account-page__avatar-section">
                        <div className="edit-account-page__avatar">
                            <div className="edit-account-page__avatar-inner">
                                {renderImagePreview()}
                            </div>
                            {previewImage && (
                                <button
                                    type="button"
                                    onClick={removeImage}
                                    className="edit-account-page__avatar-remove-btn"
                                    aria-label="Supprimer la photo"
                                >
                                    <SvgIcons
                                        name="close"
                                        width="20"
                                        height="20"
                                        color="#FFFFFF"
                                    />
                                </button>
                            )}
                        </div>
                        
                        <label className="edit-account-page__file-input">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                disabled={loading}
                            />
                            Changer la photo
                        </label>
                        
                        {errors.picture && (
                            <span className="edit-account-page__error">
                                {errors.picture}
                            </span>
                        )}
                    </div>

                    <div className="edit-account-page__input-group">
                        <label htmlFor="firstname">Prénom</label>
                        {loading ? (
                            <Skeleton
                                width="100%"
                                height="48px"
                                borderRadius="4px"
                                bkgColor="#EEE6DB"
                                animColor="#F5F0ED"
                            />
                        ) : (
                            <input
                                type="text"
                                id="firstname"
                                name="firstname"
                                value={formData.firstname}
                                onChange={handleChange}
                                className={errors.firstname ? 'error' : ''}
                                autoComplete="given-name"
                            />
                        )}
                        {errors.firstname && (
                            <span className="edit-account-page__error">
                                {errors.firstname}
                            </span>
                        )}
                    </div>

                    <div className="edit-account-page__input-group">
                        <label htmlFor="lastname">Nom</label>
                        {loading ? (
                            <Skeleton
                                width="100%"
                                height="48px"
                                borderRadius="4px"
                                bkgColor="#EEE6DB"
                                animColor="#F5F0ED"
                            />
                        ) : (
                            <input
                                type="text"
                                id="lastname"
                                name="lastname"
                                value={formData.lastname}
                                onChange={handleChange}
                                className={errors.lastname ? 'error' : ''}
                            />
                        )}
                        {errors.lastname && (
                            <span className="edit-account-page__error">
                                {errors.lastname}
                            </span>
                        )}
                    </div>

                    <div className="edit-account-page__input-group">
                        <label htmlFor="email">Email</label>
                        {loading ? (
                            <Skeleton
                                width="100%"
                                height="48px"
                                borderRadius="4px"
                                bkgColor="#EEE6DB"
                                animColor="#F5F0ED"
                            />
                        ) : (
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={errors.email ? 'error' : ''}
                            />
                        )}
                        {errors.email && (
                            <span className="edit-account-page__error">
                                {errors.email}
                            </span>
                        )}
                    </div>


                    <div className="edit-account-page__input-group">
                        <label htmlFor="telephone">Téléphone</label>
                        {loading ? (
                            <Skeleton width="100%" height="48px" borderRadius="4px" bkgColor="#EEE6DB" animColor="#F5F0ED" />
                        ) : (
                            <input type="tel" id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} />
                        )}
                    </div>

                    <div className="edit-account-page__input-group">
                        <label htmlFor="codePostal">Code postal</label>
                        {loading ? (
                            <Skeleton width="100%" height="48px" borderRadius="4px" bkgColor="#EEE6DB" animColor="#F5F0ED" />
                        ) : (
                            <input type="text" id="codePostal" name="codePostal" value={formData.codePostal} onChange={handleChange} maxLength="10" />
                        )}
                    </div>

                    <div className="edit-account-page__input-group">
                        <label htmlFor="ville">Ville</label>
                        {loading ? (
                            <Skeleton width="100%" height="48px" borderRadius="4px" bkgColor="#EEE6DB" animColor="#F5F0ED" />
                        ) : (
                            <input type="text" id="ville" name="ville" value={formData.ville} onChange={handleChange} />
                        )}
                    </div>

                    <div className="edit-account-page__input-group">
                        <label htmlFor="pays">Pays</label>
                        {loading ? (
                            <Skeleton width="100%" height="48px" borderRadius="4px" bkgColor="#EEE6DB" animColor="#F5F0ED" />
                        ) : (
                            <input type="text" id="pays" name="pays" value={formData.pays} onChange={handleChange} />
                        )}
                    </div>

                    <div className="edit-account-page__checkbox-group">
                        {loading ? (
                            <Skeleton
                                width="200px"
                                height="24px"
                                borderRadius="4px"
                                bkgColor="#EEE6DB"
                                animColor="#F5F0ED"
                            />
                        ) : (
                            <label>
                                <input
                                    type="checkbox"
                                    name="newsletter"
                                    checked={formData.newsletter}
                                    onChange={handleChange}
                                />
                                Recevoir la newsletter
                            </label>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="edit-account-page__error-message">
                            {errors.submit}
                        </div>
                    )}

                    <div className="edit-account-page__actions">
                        {loading ? (
                            <>
                                <Skeleton
                                    width="120px"
                                    height="42px"
                                    borderRadius="5px"
                                    bkgColor="#EEE6DB"
                                    animColor="#F5F0ED"
                                />
                                <Skeleton
                                    width="120px"
                                    height="42px"
                                    borderRadius="5px"
                                    bkgColor="#EEE6DB"
                                    animColor="#F5F0ED"
                                />
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="edit-account-page__cancel"
                                    disabled={saving}
                                    onClick={() => navigate('/mon-compte')}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="edit-account-page__submit"
                                    disabled={saving}
                                >
                                    {saving ? "Enregistrement..." : "Enregistrer"}
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditAccountPage;